import { Boxes, Database, RefreshCw } from 'lucide-react'
import { useGfbundles } from '../hooks/useGfbundles'
import { GfbundleApps } from '@/components/GfbundleApps'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { cn } from '@/lib/utils'

const formatDate = (dateStr: string) => {
  const d = new Date(dateStr)
  if (Number.isNaN(d.getTime())) return dateStr
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
  return `${String(d.getDate()).padStart(2, '0')}-${months[d.getMonth()]}-${d.getFullYear()}`
}

function ReadyBadge({ ready }: { ready: boolean }) {
  return (
    <Badge
      variant="secondary"
      className={cn(
        'text-[10px] px-1.5 py-0 rounded-full border-transparent',
        ready ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600',
      )}
    >
      {ready ? 'Ready' : 'Not ready'}
    </Badge>
  )
}

function Mono({ children, title }: { children: React.ReactNode; title?: string }) {
  return <span className="font-mono text-xs text-muted-foreground" title={title}>{children}</span>
}

function LoadingCard() {
  return (
    <Card>
      <div className="p-4 space-y-3">
        {[1, 2].map(i => <Skeleton key={i} className="h-8 w-full" />)}
      </div>
    </Card>
  )
}

function EmptyCard({ children }: { children: React.ReactNode }) {
  return (
    <Card>
      <div className="p-8 text-center text-sm text-muted-foreground">{children}</div>
    </Card>
  )
}

/**
 * The gfbundle register and the cells. Versions are registered by gfbundle's CI once its
 * app copies are deployed, and marked ready once every cell they name is ready; a Seed
 * deploy naming a version is refused until then. Cells are recorded by the cell script.
 * Read-only: both are written by machines.
 */
export function GfbundlesPage() {
  const { versions, cells, loading, versionsError, cellsError, refetch } = useGfbundles()

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Gfbundles</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            The gfbundle versions a Seed can name in its manifest, what each is made of, and the cells
            (one D1 + one R2 bucket) its app copies run against.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={refetch} disabled={loading}>
          <RefreshCw className={cn('h-3.5 w-3.5', loading && 'animate-spin')} />
          Refresh
        </Button>
      </div>

      <div className="flex items-center gap-2 mb-3">
        <Boxes className="h-4 w-4 text-muted-foreground" />
        <h2 className="text-sm font-semibold text-foreground">Versions</h2>
      </div>
      {versionsError && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>Failed to load gfbundle versions: {versionsError}</AlertDescription>
        </Alert>
      )}
      <div className="mb-10">
        {loading ? <LoadingCard /> : versions.length === 0 ? (
          !versionsError && <EmptyCard>No gfbundle version is registered yet.</EmptyCard>
        ) : (
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Version</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Apps</TableHead>
                  <TableHead>Cells</TableHead>
                  <TableHead>Hashes</TableHead>
                  <TableHead>Created</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {versions.map(v => (
                  <TableRow key={v.version}>
                    <TableCell className="font-mono font-medium text-foreground whitespace-nowrap">{v.version}</TableCell>
                    <TableCell><ReadyBadge ready={v.ready} /></TableCell>
                    <TableCell><GfbundleApps apps={v.apps} /></TableCell>
                    <TableCell>
                      {v.cells.length === 0 && (v.pending_cells ?? []).length === 0
                        ? <span className="text-xs text-muted-foreground">—</span>
                        : (
                          <div className="flex flex-col gap-0.5">
                            {v.cells.map(c => <Mono key={c}>{c}</Mono>)}
                            {(v.pending_cells ?? []).map(c => (
                              <span key={`pending-${c}`} className="text-muted-foreground" title="Registered, not yet marked ready: no Seed is deployed against it">
                                <Mono>{c}</Mono> <span className="text-xs">(pending)</span>
                              </span>
                            ))}
                          </div>
                        )}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-0.5">
                        {Object.entries(v.hashes).sort(([a], [b]) => a.localeCompare(b)).map(([app, hash]) => (
                          <Mono key={app} title={`${app} ${hash}`}>{app} {hash.slice(0, 12)}</Mono>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap" title={v.created_at}>
                      {formatDate(v.created_at)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        )}
      </div>

      <div className="flex items-center gap-2 mb-3">
        <Database className="h-4 w-4 text-muted-foreground" />
        <h2 className="text-sm font-semibold text-foreground">Cells</h2>
      </div>
      {cellsError && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>Failed to load cells: {cellsError}</AlertDescription>
        </Alert>
      )}
      {loading ? <LoadingCard /> : cells.length === 0 ? (
        !cellsError && <EmptyCard>No cell is recorded yet.</EmptyCard>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Cell</TableHead>
                <TableHead>Env</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>D1</TableHead>
                <TableHead>Bucket</TableHead>
                <TableHead>Jurisdiction</TableHead>
                <TableHead>Created</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {cells.map(c => (
                <TableRow key={c.name}>
                  <TableCell className="font-mono font-medium text-foreground whitespace-nowrap">{c.name}</TableCell>
                  <TableCell className="text-xs">{c.env}</TableCell>
                  <TableCell><ReadyBadge ready={c.ready} /></TableCell>
                  <TableCell><Mono title={c.d1_id}>{c.d1_name}</Mono></TableCell>
                  <TableCell><Mono>{c.bucket}</Mono></TableCell>
                  <TableCell className="text-xs uppercase text-muted-foreground">{c.jurisdiction}</TableCell>
                  <TableCell className="text-xs text-muted-foreground whitespace-nowrap" title={c.created_at}>
                    {formatDate(c.created_at)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  )
}
